import {
  attributesToObject,
  camelToKebabCase,
  EventHub,
  modernCSS,
} from "./helpers.js";

import { CSSCache, HTMLCache } from "./state.js";

/** @type {string[]} */
const componentsByUs = [];

/** @type {Map<string, Function>} */
const HMROverride = new Map();

/** @type {Map<string, Promise<void>>} */
const htmlFetches = new Map();
/** @type {Map<string, Promise<void>>} */
const cssFetches = new Map();

/** @type {Map<string, string>} */
const lastPropChange = new Map();

/** @typedef FunctionComponentOptions
 * @prop {string} [metaUrl]
 * @prop {string[]} [observedAttributes]
 * @prop {string} [name]
 * @prop {ShadowRootMode} [shadowRootMode]
 */

/**
 * @param {Function} functionalComponent
 * @param {FunctionComponentOptions} options
 * @returns {CustomElementConstructor}
 */
function _generateFunctionComponen(
  functionalComponent,
  { metaUrl, observedAttributes, name: kebabName, shadowRootMode },
) {
  return class extends HTMLElement {
    constructor() {
      super();

      this._postRender = undefined;
      this._propsChanged = undefined;
      this._hmrUpdate = false;
      this._componentPath = metaUrl;
      this._customThis = null;
      this._cacheKeyId = Math.random().toString(36).substr(2, 9);
      /**
       * @type {Record<string, string> | null}
       */
      this._cachedProps = null;

      this._hasRendered = false;

      document.addEventListener("esm-hmr:webact-function-component", () => {
        if (!kebabName) return;

        this._hmrUpdate = true;

        functionalComponent = HMROverride.has(kebabName)
          ? (/** @type {Function} */ (HMROverride.get(kebabName)))
          : functionalComponent;

        this._render(this._props ?? {});
      });
    }

    set _html(node) {
      if (!HTMLCache.has(this.htmlPath) || this._hmrUpdate) {
        const templateElement = document.createElement("template");

        if (node) {
          templateElement.content.appendChild(node);
        }

        HTMLCache.set(this.htmlPath, templateElement);
      }
    }

    /**
     * @returns {Node | null}
     */
    get _html() {
      return HTMLCache.has(this.htmlPath)
        ? (/** @type {HTMLTemplateElement} */ (HTMLCache.get(this.htmlPath))).content.cloneNode(true)
        : null;
    }

    set _css(cssStyleSheet) {
      if (!CSSCache.has(this.htmlPath) || this._hmrUpdate) {
        if (cssStyleSheet) {
          CSSCache.set(this.htmlPath, cssStyleSheet);
        }
      }
    }

    get _css() {
      return CSSCache.has(this.htmlPath) ? CSSCache.get(this.htmlPath) : null;
    }

    get cssPath() {
      const basePath = this._componentPath?.replace(/\.(html|js)/gi, ".css");
      return basePath || `__component_${kebabName}_${this._cacheKeyId}__.css`;
    }

    get htmlPath() {
      const basePath = this._componentPath?.replace(/\.(css|js)/gi, ".html");
      return basePath || `__component_${kebabName}_${this._cacheKeyId}__.html`;
    }

    static get observedAttributes() {
      return observedAttributes;
    }

    /**
     * @param {Record<string, string>} props
     */
    async _render(props) {
      this._events?.offAll();
      this._rendering = functionalComponent.apply(this.customThis, [props]);

      if (this._rendering instanceof Promise) {
        await this._rendering;
      }

      // Consolidate all DOM updates into a single RAF for better performance
      requestAnimationFrame(() => {
        // Apply CSS stylesheets
        if (CSSCache.has(this.cssPath)) {
          if (this._sDOM && "adoptedStyleSheets" in this._sDOM) {
            if (CSSCache.has(this.cssPath)) {
              this._sDOM.adoptedStyleSheets = [/** @type {CSSStyleSheet} */ (CSSCache.get(this.cssPath))];
            }
          }

          /* TODO: Make it work with old style element again maybe?
          if (this._css instanceof HTMLStyleElement) {
            this._sDOM.appendChild(this._css);
          }
            */
        } else if (document.location.href.includes("localhost")) {
          console.warn(`<${kebabName}>: Missing CSS. Will render without it.`);
        }

        // Apply HTML template
        if (this._sDOM && HTMLCache.has(this.htmlPath)) {
          this._sDOM.appendChild(
            (/** @type {HTMLTemplateElement} */ (HTMLCache.get(this.htmlPath))).content.cloneNode(true),
          );
        } else if (document.location.href.includes("localhost")) {
          console.warn(`<${kebabName}>: Missing HTML. Will render without it.`);
        }

        if (this._postRender instanceof Function) {
          queueMicrotask(() => {
            if (this._postRender) {
              this._postRender();
            }
            
            this._hmrUpdate = false;
            this._hasRendered = true;
          });
        }
      });
    }

    get _props() {
      if (!this._cachedProps) {
        this._cachedProps = attributesToObject(this.attributes);
      }
      return this._cachedProps;
    }

    get customThis() {
      if (this._customThis) {
        return this._customThis;
      }

      this._customThis = {
        /**
         * @param {TemplateStringsArray} strings
         * @param {...any} rest
         * @returns {Node | null}
         */
        html: (strings, ...rest) => {
          if (
            // A template has already been provided. Only allowed once. Ignore subsequent attempts.
            HTMLCache.has(this.htmlPath) &&
            // HMR is an expection though.
            this._hmrUpdate === false
          ) {
            return null;
          }

          const template = document.createElement("template");
          template.innerHTML = String.raw(strings, ...rest);

          HTMLCache.set(this.htmlPath, template);

          return template.content.cloneNode(true);
        },
        /**
         * @param {TemplateStringsArray} strings
         * @param {...any} rest
         * @returns {CSSStyleSheet | null}
         */
        css: (strings, ...rest) => {
          // A stylesheet has already been provided. Only allowed once. Ignore subsequent attempts.
          if (
            // A template has already been provided. Only allowed once. Ignore subsequent attempts.
            CSSCache.has(this.cssPath) &&
            // HMR is an expection though.
            this._hmrUpdate === false
          ) {
            return null;
          }

          const cssStyleSheet = modernCSS(strings, ...rest);

          CSSCache.set(this.cssPath, cssStyleSheet);

          return cssStyleSheet;
        },
        /**
         * @param {string | URL} [path]
         * @returns {Promise<void>}
         */
        useHTML: (path) => {
          const resolvedPath = path instanceof URL ? path.toString() : (path || (this._componentPath ? this.htmlPath : null));

          if (!resolvedPath) {
            return Promise.resolve();
          }

          if (!this._hmrUpdate && HTMLCache.has(resolvedPath)) {
            return Promise.resolve();
          }

          // If another instance of this component is fetching HTML, then don't fetch again. Wait for same HTML file.
          if (kebabName && htmlFetches.has(kebabName)) {
            return /** @type {Promise<void>} */ (htmlFetches.get(kebabName));
          }

          path = resolvedPath;

          const htmlFetching = async () => {
            try {
              const response = await fetch(path);
              const text = await response.text();
              const template = document.createElement("template");
              template.innerHTML = text;
              HTMLCache.set(this.htmlPath, template);
            } finally {
              if (kebabName) {
                htmlFetches.delete(kebabName);
              }
            }
          };

          const promise = htmlFetching();

          if (kebabName) {
            htmlFetches.set(kebabName, promise);
          }

          return promise;
        },
        /**
         * @param {string | URL} [path]
         * @returns {Promise<void>}
         */
        useCSS: (path) => {
          const resolvedPath = path instanceof URL ? path.toString() : (path || (this._componentPath ? this.cssPath : null));

          if (!resolvedPath) {
            return Promise.resolve();
          }

          if (!this._hmrUpdate && CSSCache.has(resolvedPath)) {
            return Promise.resolve();
          }

          // If another instance of this component is fetching CSS, then don't fetch again. Wait for same CSS file.
          if (kebabName && cssFetches.has(kebabName)) {
            return /** @type {Promise<void>} */ (cssFetches.get(kebabName));
          }

          path = resolvedPath;

          const cssFetching = async () => {
            try {
              const response = await fetch(path);
              const text = await response.text();

              CSSCache.set(this.cssPath, modernCSS`${text}`);
            } finally {
              if (kebabName) {
                cssFetches.delete(kebabName);
              }
            }
          };

          const promise = cssFetching();

          if (kebabName) {
            cssFetches.set(kebabName, promise);
          }

          return promise;
        },
        /**
         * Fetch HTML and CSS in parallel.
         * @returns {Promise<void>}
         */
        useAssets: () => Promise.all([
          this.customThis.useCSS(),
          this.customThis.useHTML(),
        ]).then(() => {}),
        /**
         * @param {Function} method
         */
        postRender: (method) => {
          this._postRender = method;
        },
        /**
         * @param {Function} method
         */
        deRender: (method) => {
          this._deRender = method;
        },
        /**
         * @param {Function} method
         */
        propsChanged: (method) => {
          this._propsChanged = method;
        },
        /**
         * @param {string} selector
         */
        $: (selector) => {
          if (selector === undefined || selector === ":host") {
            return this;
          }

          if (selector === ":root") {
            return this._sDOM;
          }

          return this._sDOM?.querySelector(selector);
        },
        /**
         * @param {string} selector
         */
        $$: (selector) => this._sDOM?.querySelectorAll(selector),
        /**
         * @param {string} type
         * @param {string} selector
         * @param {EventListener} fn
         * @param {EventListenerOptions} options
         */
        on: (type, selector, fn, options) =>
          this._events?.on(type, selector, fn, options),
        offAll: () => this._events?.offAll(),
      };

      return this._customThis;
    }

    async attributeChangedCallback() {
      this._cachedProps = null;

      if (this._rendering instanceof Promise) {
        await this._rendering;
      }

      // Only use RAF if we actually have a propsChanged handler
      if (this._propsChanged instanceof Function) {
        const serializedChange = JSON.stringify(
          attributesToObject(this.attributes),
        );

        // Avoid emitting propChanges when no props change, even if the attributeChangedCallback is run.
        if (kebabName && lastPropChange.get(kebabName) === serializedChange) {
          return;
        }

        // Use RAF only for the actual prop change handling
        requestAnimationFrame(() => {
          if (this._propsChanged) {
            this._propsChanged(attributesToObject(this.attributes));
          }

          if (kebabName) {
            lastPropChange.set(kebabName, serializedChange);
          }
        });
      } else if (document.location.href.includes("localhost")) {
        console.error(`
          <${kebabName}>: Attribute has changed and you are observing attributes, but not handling them in a propsChanged handler.
          Remove observedAttributes or or actually use them.
        `);
      }
    }

    connectedCallback() {
      this._sDOM = this.attachShadow({
        mode: shadowRootMode || "closed",
      });
      this._events = new EventHub(this._sDOM);

      this._render(this._props);
    }

    disconnectedCallback() {
      this._events?.offAll();

      if (this._deRender) {
        this._deRender();
      }
    }
  };
}

/**
 * @param {Function} functionComponent
 * @param {FunctionComponentOptions} options
 * @returns {string} Custom element tag name.
 */
export default function registerFunctionComponent(
  functionComponent,
  { metaUrl, observedAttributes, name, shadowRootMode } = {
    metaUrl: undefined,
    observedAttributes: [],
    name: undefined,
    shadowRootMode: "closed",
  },
) {
  const kebabName = name || camelToKebabCase(functionComponent.name);

  if (customElements.get(kebabName)) {
    if (componentsByUs.includes(kebabName)) {
      HMROverride.set(kebabName, functionComponent);
      document.dispatchEvent(
        new CustomEvent("esm-hmr:webact-function-component"),
      );
    } else {
      throw new Error(`
        Some else has already registered <${kebabName}> in the custom element registry.
      `);
    }
  } else {
    const customElementClass = _generateFunctionComponen(functionComponent, {
      metaUrl,
      observedAttributes,
      name: kebabName,
      shadowRootMode,
    });

    customElements.define(kebabName, customElementClass);
    componentsByUs.push(kebabName);
  }

  return kebabName;
}
